import { formatCurrency } from '@angular/common';
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { getCurrencySymbolWithFallback } from '@sg/common/src/lib/utils/currency-symbol.util';

@Pipe({
  name: 'sgCurrency',
  standalone: false,
})
export class SgCurrencyPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  /**
   * Currency formatter that consults `getCurrencySymbolWithFallback`, so codes missing from
   * Angular's CURRENCIES_EN (notably SAR) resolve to the override glyph instead of the literal
   * ISO code. By default, values < 10 (in absolute terms) render up to two decimals with
   * trailing-zero cents trimmed (e.g. 4.56 → "€4.56", 4.5 → "€4.5", 4 → "€4"); values ≥ 10 render
   * with no decimals (e.g. 12,450 → "€12,450"). Pass an explicit `digitsInfo` (e.g. '1.2-2') to
   * override the adaptive default when a fixed precision is required. Null/undefined → `fallback`.
   */
  transform(
    value: number | null | undefined,
    currencyCode: string | null | undefined,
    digitsInfo?: string,
    fallback: string = '-'
  ): string {
    return SgCurrencyPipe.transform(value, currencyCode, this.locale, digitsInfo, fallback);
  }

  static transform(
    value: number | null | undefined,
    currencyCode: string | null | undefined,
    locale: string,
    digitsInfo?: string,
    fallback: string = '-'
  ): string {
    if (value == null || !Number.isFinite(value)) return fallback;
    if (!currencyCode) return fallback;

    const symbol = getCurrencySymbolWithFallback(currencyCode, 'narrow', locale);
    const resolvedDigitsInfo = digitsInfo ?? SgCurrencyPipe.resolveDigitsInfo(value);

    return formatCurrency(value, locale, symbol, currencyCode, resolvedDigitsInfo);
  }

  private static resolveDigitsInfo(value: number): string {
    if (Math.abs(value) >= 10) return '1.0-0';

    // The 1e-10 nudge dodges IEEE-754 multiplication artifacts: 1.005 * 100 evaluates to
    // 100.4999…, which would otherwise round down and pick the wrong digits bucket ($1 vs $1.01).
    const cents = Math.round((Math.abs(value) + 1e-10) * 100) % 100;
    if (cents === 0) return '1.0-0';
    if (cents % 10 === 0) return '1.1-1';

    return '1.2-2';
  }
}
